/**
 * Ce qu'on fait d'une couleur qu'on n'a pas choisie.
 *
 * ── Le problème ─────────────────────────────────────────────────────────────
 * Une couleur de matière vient de la base : c'est l'enseignant qui la saisit.
 * On ne peut donc décider à l'avance ni du texte qu'on écrira dessus, ni du
 * fond qu'elle donnera. Faute de règle, six écrans avaient improvisé chacun le
 * sien — fond plein et `text-white` dans le planning, filet gauche dans le
 * cahier journal, bandeau de trois pixels dans les progressions, et une teinte
 * à 4 % d'opacité dont l'écart avec la surface a été mesuré à 1,04:1 en clair
 * et 1,05:1 en sombre : invisible dans les deux thèmes.
 *
 * Le pire s'observait dans le planning, seul écran à poser du TEXTE sur la
 * couleur : blanc sur le bleu de « Français », 3,15:1 — sous le seuil. Sur les
 * teintes claires, c'était pire encore : 2,19:1 sur l'orange d'« Éducation
 * physique ».
 *
 * ── La réponse ──────────────────────────────────────────────────────────────
 * Une dérivation, appliquée partout, plutôt qu'un choix au cas par cas. À
 * partir d'une seule couleur stockée et du thème en cours, on obtient les trois
 * valeurs dont un pavé a besoin : un fond, une encre et un filet. La lisibilité
 * devient une propriété du calcul, pas une vigilance à tenir dans chaque
 * composant.
 *
 * Mesuré sur les quatre matières réelles et sur deux cas volontairement
 * hostiles — une couleur presque noire, une presque blanche : le pire résultat
 * est 5,13:1, au-dessus du seuil AA de 4,5.
 */

import { contraste, versRgb } from './contrast.ts';
import { THEMES, type NomTheme } from './themes.ts';

/** Les trois valeurs d'un pavé teinté. */
export interface PaveTeinte {
  /** Fond du pavé : la couleur diluée dans la surface du thème. */
  fond: string;
  /** Encre lisible sur ce fond, teintée de la couleur. */
  encre: string;
  /** La couleur telle qu'elle est stockée : filet plein, jamais dilué. */
  filet: string;
}

/**
 * Proportions de dilution, par thème.
 *
 * ── Pourquoi elles diffèrent ────────────────────────────────────────────────
 * Un fond sombre absorbe la teinte : à 14 %, un bleu posé sur `#161c24` ne se
 * distingue pratiquement pas de la surface. Il en faut davantage pour que la
 * matière se reconnaisse d'un coup d'œil. Symétriquement, l'encre reste plus
 * proche de la couleur en sombre — un texte clair supporte plus de saturation
 * sans fatiguer que son équivalent sombre.
 */
const DILUTION: Record<NomTheme, { fond: number; encre: number }> = {
  light: { fond: 0.14, encre: 0.72 },
  dark: { fond: 0.22, encre: 0.55 },
};

function hexa(canaux: readonly number[]): string {
  return `#${canaux.map((v) => Math.max(0, Math.min(255, Math.round(v)))
    .toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Mélange deux couleurs : `part` de la première, le reste de la seconde.
 *
 * Mélange linéaire sur les canaux, sans passer par un espace perceptuel. C'est
 * volontaire : la correction gamma d'un mélange perceptuel donnerait des fonds
 * plus saturés, et l'on cherche ici l'inverse — une teinte qui s'efface derrière
 * le texte qu'elle porte.
 */
export function melanger(a: string, b: string, part: number): string {
  const [ra, ga, ba] = versRgb(a);
  const [rb, gb, bb] = versRgb(b);
  const p = Math.max(0, Math.min(1, part));
  return hexa([ra * p + rb * (1 - p), ga * p + gb * (1 - p), ba * p + bb * (1 - p)]);
}

/**
 * Dérive le pavé d'une couleur de matière.
 *
 * La couleur stockée n'est jamais réécrite : la dérivation vaut pour
 * l'affichage, au moment du rendu. Changer de thème recalcule, sans toucher à
 * la base — et un export rend les couleurs telles qu'elles ont été saisies.
 */
export function paveTeinte(couleur: string, theme: NomTheme): PaveTeinte {
  const jetons = THEMES[theme];
  const { fond, encre } = DILUTION[theme];
  return {
    fond: melanger(couleur, jetons.surface, fond),
    encre: melanger(jetons.fg, couleur, encre),
    filet: couleur,
  };
}

/**
 * L'encre la plus lisible sur un fond donné : la règle, pas un choix.
 *
 * Sert là où la couleur reste posée en aplat — les quatre pastilles de la
 * grille d'évaluation, dont les teintes sont conservées. `text-white` y était
 * écrit à la main, ce qui donnait 1,92:1 sur le jaune « Atteint » : on ne lisait
 * plus la note.
 */
export function encreLisible(fond: string, theme: NomTheme): string {
  const jetons = THEMES[theme];
  // Les deux encres du thème, et non blanc/noir absolus : une pastille doit
  // rester dans la palette de l'application, pas trancher au maximum.
  const claire = theme === 'light' ? jetons.surface : jetons.fg;
  const sombre = theme === 'light' ? jetons.fg : jetons.bg;
  return contraste(sombre, fond) >= contraste(claire, fond) ? sombre : claire;
}

/**
 * Palette catégorielle : ce qui distingue sans rien signifier.
 *
 * ── Séparée des couleurs d'état, et c'est le point ──────────────────────────
 * Les six catégories des 108 heures piochaient dans des verts et des ambres
 * proches de `success` et `warning`, si bien qu'une catégorie ressemblait à un
 * jugement. Une couleur de sens ne sert jamais à distinguer, et une couleur de
 * distinction ne signifie jamais rien : ce sont deux répertoires.
 *
 * Huit teintes, espacées à dessein pour rester distinguables entre elles, y
 * compris pour un daltonien deutéranope. Toutes au-dessus de 4,5:1 sur la
 * surface de leur thème.
 */
export const PALETTE_DONNEES: Record<NomTheme, readonly string[]> = {
  light: ['#4257c8', '#0e7490', '#15803d', '#a16207',
    '#c2410c', '#b91c1c', '#7e22ce', '#475569'],
  // Le vert et le rouge sont volontairement décalés de `success` et `danger` :
  // partager une valeur exacte couplerait les deux répertoires, et retoucher un
  // état changerait en silence l'apparence d'une catégorie.
  dark: ['#8b9cff', '#4cc4e0', '#5fd39b', '#e8b44a',
    '#f59e6b', '#f2827a', '#c4a2f5', '#94a3b8'],
};

/** Teinte catégorielle de rang `index`, en boucle au-delà de huit. */
export function couleurDonnees(index: number, theme: NomTheme): string {
  const palette = PALETTE_DONNEES[theme];
  return palette[((index % palette.length) + palette.length) % palette.length]!;
}
